import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { isBlockingPayment } from '../../hooks/useStudentAccess';
import { useBankTransferInfo } from '../../hooks/useBankTransferInfo';
import { downloadInvoicePdf } from '../../lib/invoiceDownload';
import {
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  CreditCard,
  Loader2,
  X,
  BookOpen,
  Calendar,
  Banknote,
  Receipt,
  Download,
  Landmark,
} from 'lucide-react';

interface Plan {
  id: string;
  class_id: string | null;
  plan_type: string;
  total_amount: number;
  monthly_amount: number | null;
  start_date: string;
  end_date: string | null;
  access_until: string | null;
  status: string;
  manual_override: boolean;
  stripe_subscription_id: string | null;
}

interface Payment {
  id: string;
  amount: number;
  currency: string;
  due_date: string;
  paid_date: string | null;
  status: string;
  payment_method: string | null;
  invoice_url: string | null;
  notes: string | null;
}

interface ClassInfo { id: string; title: string; }

interface InvoiceRef {
  id: string;
  payment_id: string | null;
  invoice_number: string;
  pdf_path: string | null;
}

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '–';
const fmtShort = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('de-DE') : '–';
const fmtAmount = (n: number) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const STATUS_BADGE: Record<string, string> = {
  paid:      'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800',
  open:      'bg-gray-50 dark:bg-neutral-800 text-gray-400 dark:text-neutral-500 border border-gray-200 dark:border-neutral-800',
  overdue:   'bg-[#970000]/10 dark:bg-red-500/15 text-[#970000] dark:text-red-400',
  cancelled: 'bg-gray-50 dark:bg-neutral-800 text-gray-400 dark:text-neutral-500 border border-gray-200 dark:border-neutral-800',
};
const STATUS_LABEL: Record<string, string> = {
  paid: 'Bezahlt', open: 'Offen', overdue: 'Überfällig', cancelled: 'Storniert',
};
const METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Überweisung', cash: 'Bar', stripe: 'Online', other: 'Sonstiges',
};

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[#f3ede8] dark:border-neutral-800 last:border-0">
      <span className="text-sm text-gray-500 dark:text-neutral-400">{label}</span>
      <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100 text-right">{value}</span>
    </div>
  );
}

const StudentPaymentsPage: React.FC = () => {
  const { user, loading: authLoading } = useStudentAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRef[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const bankInfo = useBankTransferInfo(
    payments.some(p => p.status === 'open' || p.status === 'overdue')
  );

  const [successMsg] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('success')) {
      window.history.replaceState({}, '', window.location.pathname);
      return true;
    }
    return false;
  });
  const [showSuccess, setShowSuccess] = useState(successMsg);

  useEffect(() => {
    if (!showSuccess) return;
    const t = setTimeout(() => setShowSuccess(false), 8000);
    return () => clearTimeout(t);
  }, [showSuccess]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoadError(null);
    try {
      const { data: planData, error: planError } = await supabase
        .from('student_payment_plans')
        .select('*')
        .eq('student_id', user.id)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (planError) throw planError;

      if (planData) {
        setPlan(planData as Plan);
        const { data: pays, error: paysError } = await supabase
          .from('student_payments')
          .select('*')
          .eq('plan_id', planData.id)
          .order('due_date');
        if (paysError) throw paysError;
        setPayments((pays ?? []) as Payment[]);

        if (planData.class_id) {
          const { data: cls } = await supabase
            .from('portal_classes')
            .select('id, title')
            .eq('id', planData.class_id)
            .maybeSingle();
          setClassInfo(cls as ClassInfo ?? null);
        }
      }

      // Eigene Rechnungen (RLS: nur die des eingeloggten Schülers)
      const { data: invs } = await supabase
        .from('invoices')
        .select('id, payment_id, invoice_number, pdf_path')
        .eq('student_id', user.id)
        .eq('status', 'issued');
      setInvoices((invs ?? []) as InvoiceRef[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error('StudentPaymentsPage load error:', err);
      setLoadError('Zahlungsdaten konnten nicht geladen werden. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const handlePayNow = async () => {
    if (!plan || !user) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    let redirecting = false;
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { plan_id: plan.id },
      });
      if (error) {
        let detail = 'Zahlung konnte nicht gestartet werden.';
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx) {
            const raw = await ctx.text();
            const parsed = JSON.parse(raw);
            if (parsed.error) detail = parsed.error;
          }
        } catch { /* ignore */ }
        setCheckoutError(detail);
        return;
      }
      if (data?.url) {
        const isStripeUrl = /^https:\/\/(checkout|billing)\.stripe\.com\//.test(data.url);
        if (!isStripeUrl) {
          setCheckoutError('Ungültige Zahlungs-URL erhalten. Bitte kontaktiere den Support.');
          return;
        }
        redirecting = true;
        window.location.href = data.url;
      } else {
        setCheckoutError(data?.error ?? 'Fehler beim Erstellen der Zahlungsseite.');
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('Stripe checkout error:', err);
      setCheckoutError('Netzwerkfehler. Bitte prüfe deine Verbindung und versuche es erneut.');
    } finally {
      if (!redirecting) setCheckoutLoading(false);
    }
  };

  const invoiceFor = (paymentId: string): InvoiceRef | null =>
    invoices.find(i => i.payment_id === paymentId && i.pdf_path) ?? null;

  const handleDownloadInvoice = async (inv: InvoiceRef) => {
    if (!inv.pdf_path || downloadingId) return;
    setDownloadingId(inv.id);
    try {
      await downloadInvoicePdf(inv.pdf_path, inv.invoice_number);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Invoice download error:', err);
      setCheckoutError('Rechnung konnte nicht heruntergeladen werden. Bitte versuche es später erneut.');
    } finally {
      setDownloadingId(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const hasOverdue = payments.some(p => p.status === 'overdue');
  const hasOpen = payments.some(p => p.status === 'open');
  // Gesperrt erst nach Ablauf der Karenzzeit — gleiche Logik wie useStudentAccess,
  // inklusive der Plan-Ausnahmen (manuelle Freischaltung, bezahlter Zeitraum).
  const planOverride = !!plan && (
    plan.manual_override ||
    (!!plan.access_until && plan.access_until >= today)
  );
  const isBlocked = !planOverride && payments.some(isBlockingPayment);
  const paidCount = payments.filter(p => p.status === 'paid').length;
  const hasStripeSubscription = !!plan?.stripe_subscription_id;
  const showPayButton = plan && plan.status === 'active' && (hasOpen || hasOverdue) && !hasStripeSubscription;
  const firstInvoice = payments.find(
    p => (p.status === 'open' || p.status === 'overdue') && p.invoice_url
  )?.invoice_url;
  const accessActive = plan && (
    plan.manual_override ||
    (plan.access_until && plan.access_until >= today) ||
    ((plan.status === 'active' || plan.status === 'completed') && !isBlocked)
  );

  const overduePayment = payments.find(p => p.status === 'overdue');
  const openPayment = payments.find(p => p.status === 'open');
  const nextDue = overduePayment ?? openPayment;

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-8 w-56 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="h-28 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
        <div className="h-48 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
        <div className="h-64 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Zahlungen & Rechnungen</h1>
        <div className="flex items-start gap-3 p-5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
          <AlertCircle className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">{loadError}</p>
          </div>
          <button
            onClick={() => { setLoading(true); load(); }}
            className="text-xs text-[#970000] dark:text-red-400 font-semibold underline hover:no-underline shrink-0"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 dark:text-red-400 mb-1">
            Finanzen
          </p>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Zahlungen & Rechnungen</h1>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-7 w-7 text-gray-300 dark:text-neutral-600" />
          </div>
          <p className="font-semibold text-gray-900 dark:text-neutral-100 mb-1">Kein Zahlungsplan vorhanden</p>
          <p className="text-sm text-gray-400 dark:text-neutral-500">Bitte kontaktiere uns bei Fragen zu deiner Buchung.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stripe redirect overlay */}
      {checkoutLoading && (
        <div className="fixed inset-0 z-[9999] bg-white/80 dark:bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-8 w-8 text-[#970000] dark:text-red-400 animate-spin" />
          <p className="text-sm font-semibold text-gray-700 dark:text-neutral-300">Weiterleitung zu Stripe…</p>
        </div>
      )}

      {/* Page header */}
      <div>
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 dark:text-red-400 mb-1">
          Finanzen
        </p>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Zahlungen & Rechnungen</h1>
      </div>

      {/* Success banner */}
      {showSuccess && (
        <div className="flex items-start gap-3 p-5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
          <CheckCircle className="h-5 w-5 text-gray-500 dark:text-neutral-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">Zahlung erfolgreich übermittelt</p>
            <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">
              Bei Kartenzahlung ist dein Zugang sofort aktiv. Bei SEPA-Lastschrift kann es 2–5 Werktage dauern.
            </p>
          </div>
          <button
            onClick={() => setShowSuccess(false)}
            aria-label="Hinweis schließen"
            className="text-gray-400 dark:text-neutral-500 hover:text-gray-500 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Checkout error */}
      {checkoutError && (
        <div className="flex items-start gap-3 p-5 bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 rounded-2xl">
          <AlertCircle className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
          <p className="flex-1 text-sm text-gray-900 dark:text-neutral-100">{checkoutError}</p>
          <button
            onClick={() => setCheckoutError(null)}
            aria-label="Fehlermeldung schließen"
            className="text-gray-400 dark:text-neutral-500 hover:text-gray-500 dark:hover:text-neutral-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Status card ── */}
      {hasOverdue ? (
        <div className="bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5 text-[#970000] dark:text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 dark:text-neutral-100 mb-0.5">Zahlung überfällig</p>
              <p className="text-sm text-gray-500 dark:text-neutral-400">
                {isBlocked
                  ? 'Dein Zugang ist pausiert. Bitte begleiche den offenen Betrag, um wieder Zugang zu erhalten.'
                  : 'Dein Zugang ist noch aktiv. Bitte begleiche den offenen Betrag umgehend, damit dein Zugang nicht pausiert wird.'}
                {overduePayment && (
                  <> Fällig war: <span className="font-semibold text-gray-900 dark:text-neutral-100">{fmtShort(overduePayment.due_date)}</span>.</>
                )}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {firstInvoice && (
                  <a
                    href={firstInvoice}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#970000] text-[#970000] dark:text-red-400 rounded-xl text-sm font-semibold hover:bg-[#970000]/5 dark:hover:bg-red-500/10 transition-colors"
                  >
                    <Receipt className="h-4 w-4" />
                    Rechnung öffnen
                  </a>
                )}
                {showPayButton && (
                  <button
                    onClick={handlePayNow}
                    disabled={checkoutLoading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#970000] text-white rounded-xl text-sm font-semibold hover:bg-[#7a0000] disabled:opacity-60 transition-colors"
                  >
                    {checkoutLoading
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CreditCard className="h-4 w-4" />
                    }
                    Jetzt bezahlen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : hasOpen ? (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-gray-400 dark:text-neutral-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 dark:text-neutral-100 mb-0.5">Offene Zahlung</p>
              <p className="text-sm text-gray-500 dark:text-neutral-400">
                Dein Zugang ist noch aktiv. Bitte begleiche die Rate rechtzeitig.
                {nextDue && (
                  <> Fällig am: <span className="font-semibold text-gray-900 dark:text-neutral-100">{fmtShort(nextDue.due_date)}</span>
                    {' · '}<span className="font-semibold text-gray-900 dark:text-neutral-100">{fmtAmount(nextDue.amount)}</span>.
                  </>
                )}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {firstInvoice && (
                  <a
                    href={firstInvoice}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 dark:border-neutral-800 text-gray-500 dark:text-neutral-400 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Receipt className="h-4 w-4" />
                    Rechnung öffnen
                  </a>
                )}
                {showPayButton && (
                  <button
                    onClick={handlePayNow}
                    disabled={checkoutLoading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#970000] text-white rounded-xl text-sm font-semibold hover:bg-[#7a0000] disabled:opacity-60 transition-colors"
                  >
                    {checkoutLoading
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CreditCard className="h-4 w-4" />
                    }
                    Jetzt bezahlen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : accessActive && !showSuccess ? (
        <div className="flex items-center gap-3 p-5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
          <CheckCircle className="h-5 w-5 text-gray-500 dark:text-neutral-400 shrink-0" />
          <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">Dein Zugang ist aktiv. Alles in Ordnung.</p>
        </div>
      ) : null}

      {/* ── Zahlungsmöglichkeiten ── */}
      {(hasOpen || hasOverdue) && (bankInfo || showPayButton) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {bankInfo && (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
                  <Landmark className="h-4 w-4 text-[#970000] dark:text-red-400" />
                </div>
                <p className="font-bold text-gray-900 dark:text-neutral-100">Per Überweisung</p>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-400 dark:text-neutral-500 shrink-0">Empfänger</dt>
                  <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right">{bankInfo.account_holder}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-400 dark:text-neutral-500 shrink-0">IBAN</dt>
                  <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right break-all">{bankInfo.iban}</dd>
                </div>
                {bankInfo.bic && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-400 dark:text-neutral-500 shrink-0">BIC</dt>
                    <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right">{bankInfo.bic}</dd>
                  </div>
                )}
                {bankInfo.bank_name && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-400 dark:text-neutral-500 shrink-0">Bank</dt>
                    <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right">{bankInfo.bank_name}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-400 dark:text-neutral-500 shrink-0">Verwendungszweck</dt>
                  <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right">AR Kursgebühr</dd>
                </div>
                {nextDue && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-400 dark:text-neutral-500 shrink-0">Betrag</dt>
                    <dd className="text-gray-900 dark:text-neutral-100 font-semibold text-right">{fmtAmount(nextDue.amount)}</dd>
                  </div>
                )}
              </dl>
              <p className="text-xs text-gray-400 dark:text-neutral-500 leading-relaxed mt-4">
                Nach Zahlungseingang wird dein Zugang freigeschaltet (1–3 Werktage).
              </p>
            </div>
          )}

          {(showPayButton || hasStripeSubscription) && (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-6 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
                  <CreditCard className="h-4 w-4 text-[#970000] dark:text-red-400" />
                </div>
                <p className="font-bold text-gray-900 dark:text-neutral-100">Online bezahlen</p>
              </div>
              {hasStripeSubscription ? (
                <p className="text-sm text-gray-500 dark:text-neutral-400 leading-relaxed">
                  Deine Raten werden automatisch über dein Stripe-Abo abgebucht.
                  Du musst nichts weiter tun.
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-500 dark:text-neutral-400 leading-relaxed mb-5">
                    Sofort per Karte, SEPA-Lastschrift oder Link zahlen.
                    Bei Kartenzahlung ist dein Zugang direkt wieder aktiv.
                  </p>
                  <button
                    onClick={handlePayNow}
                    disabled={checkoutLoading}
                    className="mt-auto w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#970000] text-white rounded-2xl text-sm font-semibold hover:bg-[#7a0000] disabled:opacity-60 transition-colors"
                  >
                    {checkoutLoading
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CreditCard className="h-4 w-4" />}
                    Jetzt mit Stripe zahlen
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Plan summary cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
            <BookOpen className="h-4 w-4 text-[#970000] dark:text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-400 dark:text-neutral-500 mb-0.5">Kurs</p>
            <p className="text-sm font-bold text-gray-900 dark:text-neutral-100 leading-snug">
              {classInfo?.title ?? '–'}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
            <Banknote className="h-4 w-4 text-[#970000] dark:text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-400 dark:text-neutral-500 mb-0.5">
              {plan.plan_type === 'monthly' ? 'Monatliche Rate' : 'Gesamtbetrag'}
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-neutral-100">
              {plan.plan_type === 'monthly' && plan.monthly_amount != null
                ? fmtAmount(plan.monthly_amount)
                : fmtAmount(plan.total_amount)}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
            <Calendar className="h-4 w-4 text-[#970000] dark:text-red-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-400 dark:text-neutral-500 mb-0.5">
              {plan.access_until ? 'Zugriff bis' : 'Kursbeginn'}
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-neutral-100">
              {plan.access_until ? fmtShort(plan.access_until) : fmtShort(plan.start_date)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Plan details ── */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-neutral-800">
          <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">Vertrag</p>
          <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Kursvertrag</h2>
        </div>
        <div className="px-6 py-2">
          <InfoRow label="Gebuchter Kurs" value={classInfo?.title ?? '–'} />
          <InfoRow
            label="Zahlungsplan"
            value={plan.plan_type === 'monthly' ? 'Ratenzahlung' : 'Einmalzahlung'}
          />
          {plan.plan_type === 'monthly' && plan.monthly_amount != null && (
            <InfoRow label="Monatliche Rate" value={fmtAmount(plan.monthly_amount)} />
          )}
          <InfoRow label="Gesamtbetrag" value={fmtAmount(plan.total_amount)} />
          <InfoRow label="Kursbeginn" value={fmt(plan.start_date)} />
          {plan.end_date && <InfoRow label="Kursende" value={fmt(plan.end_date)} />}
          {plan.access_until && <InfoRow label="Zugriff bis" value={fmt(plan.access_until)} />}
        </div>
      </div>

      {/* ── Payments list ── */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-neutral-800 flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">Übersicht</p>
            <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Zahlungshistorie</h2>
          </div>
          {payments.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-neutral-500 pb-0.5">
              {paidCount} / {payments.length} bezahlt
            </p>
          )}
        </div>

        {payments.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Receipt className="h-8 w-8 text-gray-300 dark:text-neutral-600 mx-auto mb-2" />
            <p className="text-sm text-gray-400 dark:text-neutral-500">Noch keine Zahlungen eingetragen.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-800">
                  <tr>
                    {['Fälligkeit', 'Betrag', 'Status', 'Zahlungsart', 'Rechnung'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[10px] font-bold text-gray-400 dark:text-neutral-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f3ede8] dark:divide-neutral-800">
                  {payments.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors group">
                      <td className="px-5 py-3.5 text-gray-900 dark:text-neutral-100 tabular-nums">{fmtShort(p.due_date)}</td>
                      <td className="px-5 py-3.5 font-semibold text-gray-900 dark:text-neutral-100 tabular-nums">{fmtAmount(p.amount)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[p.status] ?? 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800'}`}>
                          {STATUS_LABEL[p.status] ?? p.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 dark:text-neutral-400">
                        {p.payment_method ? (METHOD_LABEL[p.payment_method] ?? p.payment_method) : '–'}
                      </td>
                      <td className="px-5 py-3.5">
                        {(() => {
                          const inv = invoiceFor(p.id);
                          if (inv) {
                            return (
                              <button
                                onClick={() => handleDownloadInvoice(inv)}
                                disabled={downloadingId === inv.id}
                                title={inv.invoice_number}
                                className="inline-flex items-center gap-1 text-[#970000] dark:text-red-400 hover:underline text-xs font-semibold disabled:opacity-60"
                              >
                                {downloadingId === inv.id
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Download className="h-3.5 w-3.5" />}
                                PDF
                              </button>
                            );
                          }
                          if (p.invoice_url) {
                            return (
                              <a
                                href={p.invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[#970000] dark:text-red-400 hover:underline text-xs font-semibold"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Öffnen
                              </a>
                            );
                          }
                          return <span className="text-gray-300 dark:text-neutral-600">–</span>;
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile list */}
            <ul className="sm:hidden divide-y divide-[#f3ede8] dark:divide-neutral-800">
              {payments.map(p => (
                <li key={p.id} className="px-5 py-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[p.status] ?? 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400'}`}>
                        {STATUS_LABEL[p.status] ?? p.status}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">{fmtAmount(p.amount)}</p>
                    <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5">
                      Fällig {fmtShort(p.due_date)}
                      {p.payment_method && ` · ${METHOD_LABEL[p.payment_method] ?? p.payment_method}`}
                    </p>
                  </div>
                  {(() => {
                    const inv = invoiceFor(p.id);
                    if (inv) {
                      return (
                        <button
                          onClick={() => handleDownloadInvoice(inv)}
                          disabled={downloadingId === inv.id}
                          title={inv.invoice_number}
                          className="shrink-0 w-9 h-9 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center text-[#970000] dark:text-red-400 hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors disabled:opacity-60"
                        >
                          {downloadingId === inv.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Download className="h-4 w-4" />}
                        </button>
                      );
                    }
                    if (p.invoice_url) {
                      return (
                        <a
                          href={p.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 w-9 h-9 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center text-[#970000] dark:text-red-400 hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      );
                    }
                    return null;
                  })()}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentPaymentsPage;
